import { Button, Form, Input, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { extractErrorMessage } from '@shared/api';
import { useRegisterMutation } from '../api/mutations';

const { Title, Text } = Typography;

interface RegisterFormValues {
  email: string;
  password: string;
  passwordConfirm: string;
}

export function RegisterForm() {
  const navigate = useNavigate();
  const [form] = Form.useForm<RegisterFormValues>();
  const registerMutation = useRegisterMutation();

  const handleSubmit = async (values: RegisterFormValues) => {
    try {
      await registerMutation.mutateAsync({
        email: values.email,
        password: values.password,
      });
      // Передаём email на следующий шаг через state (без localStorage).
      navigate('/verify-email', { state: { email: values.email } });
    } catch {
      // см. extractErrorMessage ниже
    }
  };

  const errorMessage = registerMutation.error ? extractErrorMessage(registerMutation.error) : null;

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
        Создать аккаунт
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Зарегистрируйтесь, чтобы создавать ИИ-ассистентов
      </Text>

      <Form<RegisterFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        autoComplete="on"
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Введите email' },
            { type: 'email', message: 'Некорректный email' },
          ]}
        >
          <Input size="large" type="email" placeholder="user@example.com" autoFocus />
        </Form.Item>

        <Form.Item
          name="password"
          label="Пароль"
          rules={[
            { required: true, message: 'Придумайте пароль' },
            { min: 8, message: 'Минимум 8 символов' },
            { max: 128, message: 'Максимум 128 символов' },
          ]}
          tooltip="Не короче 8 символов"
        >
          <Input.Password size="large" placeholder="••••••••" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="passwordConfirm"
          label="Повторите пароль"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Повторите пароль' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Пароли не совпадают'));
              },
            }),
          ]}
        >
          <Input.Password size="large" placeholder="••••••••" autoComplete="new-password" />
        </Form.Item>

        {errorMessage && (
          <div style={{ marginBottom: 16, color: '#DC2626', fontSize: 13 }}>{errorMessage}</div>
        )}

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={registerMutation.isPending}
        >
          Зарегистрироваться
        </Button>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Text type="secondary">Уже есть аккаунт? </Text>
          <Link to="/login">Войти</Link>
        </div>
      </Form>
    </div>
  );
}
